// Shadow cutover (contract §6.11): the six steps as a stepper with the admin actions on their step (start / stop shadow
// sync, promote, roll back; promote and roll back confirm in two steps), the difference report with supervisor
// acceptances, and the cutover history. Reloads after every action and on Refresh.

import { useState, type ReactNode } from 'react';
import { api, errorText, type CutoverDiff, type CutoverDiffItem, type CutoverState, type ErpStatus, type PromoteResult, type RollbackResult } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, Loading, Pill, Seg } from '../../components/ui.tsx';
import { useAction, useLoad } from '../../hooks.ts';
import { href } from '../../router.ts';
import { useSession } from '../../session.tsx';
import { fmtLocal } from '../../time.ts';
import { clip, ErpHeader, human, ModeStrip, num, plural, RefreshBtn, usePoll, valText } from './shared.tsx';

type StepState = 'done' | 'now' | 'todo';

function Step({ n, title, state, children }: { n: number; title: string; state: StepState; children?: ReactNode }) {
  return (
    <li className={`st-${state}`} aria-current={state === 'now' ? 'step' : undefined}>
      <span className="mk" aria-hidden="true">
        {state === 'done' ? <Icon name="check" /> : n}
      </span>
      <div className="sb">
        <div className="stt">
          {title}
          {state === 'now' && <span className="note"> · current</span>}
        </div>
        {children}
      </div>
    </li>
  );
}

function Ready({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`rdy ${ok ? 'ok-t' : 'warn-t'}`}>
      <Icon name={ok ? 'check' : 'x'} />
      {label}
    </span>
  );
}

/* ---------- actions ---------- */

function ShadowControls({ c, onDone }: { c: CutoverState; onDone: () => void }) {
  const s = useSession();
  const [initialLoad, setInitialLoad] = useState(true);
  const [stopAsk, setStopAsk] = useState(false);
  const [requestId, setRequestId] = useState<string | null | undefined>(undefined);
  const on = useAction(async () => {
    const r = await api.cutoverShadowOn(initialLoad);
    setRequestId(r.requestId);
    onDone();
  });
  const off = useAction(async () => {
    await api.cutoverShadowOff();
    setStopAsk(false);
    onDone();
  });
  if (!s.isAdmin) return c.mode === 'off' ? <div className="note">An admin starts shadow sync.</div> : null;
  if (c.mode === 'off') {
    return (
      <div className="stact">
        <label className="ck-inline">
          <input type="checkbox" checked={initialLoad} onChange={(e) => setInitialLoad(e.target.checked)} />
          Ask the ERP for everything now (initial load)
        </label>
        <button type="button" className="btn sm pri" disabled={on.busy || !c.ready.receiver || (initialLoad && !c.ready.delivery)} onClick={() => void on.run()}>
          <Icon name="play" />
          {on.busy ? 'Starting…' : 'Start shadow sync'}
        </button>
        <InlineError error={on.error} />
      </div>
    );
  }
  if (c.mode === 'shadow') {
    return (
      <div className="stact">
        {requestId !== undefined && (
          <span className="note">
            Shadow sync on.{requestId ? <> Initial load requested: <span className="mono">{requestId}</span>.</> : ' No initial load requested.'}
          </span>
        )}
        {!stopAsk ? (
          <button type="button" className="btn sm" onClick={() => setStopAsk(true)}>
            <Icon name="pause" />
            Stop shadow sync
          </button>
        ) : (
          <div className="confirmbar">
            <span>Stop taking the ERP feed? The shadow copy is kept; master data stays on import files.</span>
            <button type="button" className="btn sm pri" disabled={off.busy} onClick={() => void off.run()}>
              {off.busy ? 'Stopping…' : 'Yes, stop'}
            </button>
            <button type="button" className="btn sm" disabled={off.busy} onClick={() => setStopAsk(false)}>
              Back
            </button>
          </div>
        )}
        <InlineError error={off.error} />
      </div>
    );
  }
  return null;
}

function PromoteControls({ c, diff, held, onDone }: { c: CutoverState; diff: CutoverDiff | undefined; held: number; onDone: () => void }) {
  const s = useSession();
  const [backlog, setBacklog] = useState(false);
  const [ask, setAsk] = useState(false);
  const [result, setResult] = useState<PromoteResult | null>(null);
  const run = useAction(async () => {
    const r = await api.cutoverPromote(backlog && held > 0);
    setResult(r);
    setAsk(false);
    onDone();
  });
  const open = diff?.counts.open ?? null;
  const shadowEmpty = !!diff && diff.counts.shadow === 0;
  const blocked = c.mode !== 'shadow' || open === null || open > 0 || shadowEmpty;
  const why =
    c.mode !== 'shadow'
      ? 'Promotion needs shadow mode.'
      : open === null
        ? 'Loading the difference report…'
        : shadowEmpty
          ? 'The shadow copy is empty; wait for the initial load.'
          : open > 0
            ? `${plural(open, 'open difference')} below: fix them in the ERP or accept them first.`
            : 'The difference report is clear.';
  return (
    <div className="stact">
      {result && <PromoteSummary r={result} />}
      {c.mode === 'shadow' && (
        <>
          <span className={`note${blocked ? '' : ' ok-t'}`}>{why}</span>
          {s.isAdmin ? (
            <>
              {held > 0 && (
                <label className="ck-inline">
                  <input type="checkbox" checked={backlog} onChange={(e) => setBacklog(e.target.checked)} />
                  Also deliver the {num(held)} SOC message{held === 1 ? '' : 's'} held until cutover (OQ-17)
                </label>
              )}
              {!ask ? (
                <button type="button" className="btn sm pri" disabled={blocked} onClick={() => setAsk(true)}>
                  <Icon name="up" />
                  Promote to live…
                </button>
              ) : (
                <div className="confirmbar">
                  <span>
                    Promote the shadow copy to live in one step? Imports switch off and SOC → ERP delivery switches on. Deactivations and deletes the switch
                    causes wait as held changes. The import-based replica is kept for 30 days.
                  </span>
                  <button type="button" className="btn sm pri" disabled={run.busy} onClick={() => void run.run()}>
                    {run.busy ? 'Promoting…' : 'Yes, promote'}
                  </button>
                  <button type="button" className="btn sm" disabled={run.busy} onClick={() => setAsk(false)}>
                    Back
                  </button>
                </div>
              )}
            </>
          ) : (
            <span className="note">An admin promotes.</span>
          )}
        </>
      )}
      <InlineError error={run.error} />
    </div>
  );
}

function PromoteSummary({ r }: { r: PromoteResult }) {
  return (
    <div className="banner ok">
      <Icon name="check" />
      <span>
        Promoted. {plural(r.held, 'change')} held for a supervisor{r.held ? <> (<a href={href.imports()}>review in Imports</a>)</> : ''}, {num(r.reducingApplied)}{' '}
        reducing change{r.reducingApplied === 1 ? '' : 's'} applied, {num(r.supersededHolds)} earlier hold{r.supersededHolds === 1 ? '' : 's'} superseded,{' '}
        {num(r.acceptedDifferences)} accepted difference{r.acceptedDifferences === 1 ? '' : 's'}, {num(r.backlogQueued)} held SOC message
        {r.backlogQueued === 1 ? '' : 's'} queued. Rollback until {fmtLocal(r.rollbackUntil)}.
      </span>
    </div>
  );
}

function RollbackControls({ c, onDone }: { c: CutoverState; onDone: () => void }) {
  const s = useSession();
  const [ask, setAsk] = useState(false);
  const [result, setResult] = useState<RollbackResult | null>(null);
  const run = useAction(async () => {
    const r = await api.cutoverRollback();
    setResult(r);
    setAsk(false);
    onDone();
  });
  return (
    <div className="stact">
      {result && (
        <div className="banner ok">
          <Icon name="check" />
          <span>
            Rolled back to the import-based replica. {num(result.supersededHolds)} hold{result.supersededHolds === 1 ? '' : 's'} superseded,{' '}
            {num(result.reappliedToShadow)} held ERP change{result.reappliedToShadow === 1 ? '' : 's'} reapplied to the shadow copy. The feed is back in shadow mode.
          </span>
        </div>
      )}
      {c.mode === 'live' && (
        <span className="note">
          {c.rollbackAvailable ? (
            <>
              The import-based replica is kept until <span className="mono">{fmtLocal(c.rollbackUntil)}</span>.
            </>
          ) : (
            'No rollback: the 30-day window has ended or no import-based replica is kept.'
          )}
        </span>
      )}
      {c.rollbackAvailable && s.isAdmin && (
        <>
          {!ask ? (
            <button type="button" className="btn sm" onClick={() => setAsk(true)}>
              <Icon name="back" />
              Roll back…
            </button>
          ) : (
            <div className="confirmbar danger">
              <span>
                Switch back to the import-based replica in one step? Imports switch back on, SOC → ERP delivery stops, and the ERP feed returns to shadow
                mode. Nothing the ERP sent is lost.
              </span>
              <button type="button" className="btn sm danger" disabled={run.busy} onClick={() => void run.run()}>
                {run.busy ? 'Rolling back…' : 'Yes, roll back'}
              </button>
              <button type="button" className="btn sm" disabled={run.busy} onClick={() => setAsk(false)}>
                Back
              </button>
            </div>
          )}
        </>
      )}
      <InlineError error={run.error} />
    </div>
  );
}

function Stepper({ c, diff, status, onDone }: { c: CutoverState; diff: CutoverDiff | undefined; status: ErpStatus | undefined; onDone: () => void }) {
  const m = c.mode;
  const ready = c.ready.receiver && c.ready.delivery;
  const st = (off: StepState, shadow: StepState, live: StepState): StepState => (m === 'off' ? off : m === 'shadow' ? shadow : live);
  const shadowSites = c.activeCounts.site?.shadow ?? 0;
  return (
    <Card title={<><Icon name="route" />Cutover steps (contract §6.11)</>}>
      <ol className="stepper">
        <Step n={1} title="Build and test" state={ready ? 'done' : st('now', 'done', 'done')}>
          <div className="row-wrap">
            <Ready ok={c.ready.receiver} label="Receiver configured (ERP_TO_SOC_SECRET)" />
            <Ready ok={c.ready.delivery} label="Delivery configured (ERP_HOOK_URL, SOC_TO_ERP_SECRET)" />
          </div>
        </Step>
        <Step n={2} title="The ERP takes over import-only data" state={st(ready ? 'now' : 'todo', 'done', 'done')}>
          <div className="note">Done by the ERP team from the SOC's export, keeping every ID. Import files are frozen from then on.</div>
        </Step>
        <Step n={3} title="Shadow sync" state={st('todo', 'done', 'done')}>
          <div className="note">
            ERP messages fill a separate shadow copy with all the normal rules; operators keep using the imported data.
            {m !== 'off' && <> Shadow copy: {plural(shadowSites, 'active site')}.</>}
          </div>
          <ShadowControls c={c} onDone={onDone} />
        </Step>
        <Step n={4} title="Difference report" state={st('todo', 'now', 'done')}>
          <div className="note">
            {m === 'live'
              ? 'Cleared before the switch. After promotion there is no shadow copy to compare.'
              : diff
              ? `${plural(diff.counts.open, 'open difference')}, ${num(diff.counts.accepted)} accepted, ${num(diff.counts.same)} the same.`
              : 'Shadow vs live, entity by entity and field by field (below).'}
          </div>
        </Step>
        <Step n={5} title="Switch (promote to live)" state={st('todo', 'todo', 'done')}>
          {m === 'live' && c.promotedAt && <div className="note">Promoted {fmtLocal(c.promotedAt)}.</div>}
          <PromoteControls c={c} diff={diff} held={status?.delivery.heldUntilCutover ?? 0} onDone={onDone} />
        </Step>
        <Step n={6} title="Rollback (30 days)" state={st('todo', 'todo', c.rollbackAvailable ? 'now' : 'done')}>
          <RollbackControls c={c} onDone={onDone} />
        </Step>
      </ol>
    </Card>
  );
}

/* ---------- difference report ---------- */

const KIND_LABEL: Record<CutoverDiffItem['kind'], string> = { different: 'Different', only_in_live: 'Only in live', only_in_shadow: 'Only in shadow' };

function Fields({ it }: { it: CutoverDiffItem }) {
  const [all, setAll] = useState(false);
  if (it.kind !== 'different') return <span className="note">{it.kind === 'only_in_live' ? 'Not in the ERP feed' : 'Not in the imported data'}</span>;
  const shown = all ? it.fields : it.fields.slice(0, 3);
  return (
    <div className="fdiffs">
      {shown.map((f) => {
        const full = `${f.path}: ${valText(f.live)} → ${valText(f.shadow)}`;
        return (
          <div key={f.path} className="fd mono" title={full}>
            <span className="fp">{f.path}</span>: <span className="fl">{clip(valText(f.live), 60)}</span> → <span className="fs">{clip(valText(f.shadow), 60)}</span>
          </div>
        );
      })}
      {it.fields.length > 3 && (
        <button type="button" className="btn sm ghost" onClick={() => setAll((v) => !v)}>
          {all ? 'Fewer' : `+${it.fields.length - 3} more`}
        </button>
      )}
    </div>
  );
}

function DiffRow({ it, onDone }: { it: CutoverDiffItem; onDone: () => void }) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const accept = useAction(async () => {
    await api.cutoverAccept(it.entityType, it.entityId, note.trim());
    setAsking(false);
    setNote('');
    onDone();
  });
  const remove = useAction(async () => {
    await api.cutoverUnaccept(it.entityType, it.entityId);
    onDone();
  });
  return (
    <>
      <tr className={it.accepted ? 'inactive' : undefined}>
        <td>
          <span className={`res ${it.kind === 'different' ? 'wait' : 'bad'}`}>{KIND_LABEL[it.kind]}</span>
        </td>
        <td>
          <div>
            {it.entityType} <span className="mono dim">{it.entityId}</span>
          </div>
          {it.name && <div className="note ell-n">{it.name}</div>}
        </td>
        <td className="mono small">{it.siteId ? <a href={href.site(it.siteId)}>{it.siteId}</a> : <span className="dim">—</span>}</td>
        <td className="fcell">
          <Fields it={it} />
        </td>
        <td className="small">
          {it.accepted ? (
            <>
              <Pill tone="ok">accepted</Pill>
              <div className="note">
                {it.accepted.by} · {fmtLocal(it.accepted.at)}
              </div>
              {it.accepted.note && <div className="note ell-n" title={it.accepted.note}>“{it.accepted.note}”</div>}
            </>
          ) : (
            <Pill tone="warn">open</Pill>
          )}
        </td>
        <td className="nowrap">
          {it.accepted ? (
            <button type="button" className="btn sm" disabled={remove.busy} onClick={() => void remove.run()}>
              {remove.busy ? 'Removing…' : 'Remove'}
            </button>
          ) : (
            !asking && (
              <button type="button" className="btn sm" onClick={() => setAsking(true)}>
                <Icon name="check" />
                Accept
              </button>
            )
          )}
          <InlineError error={remove.error} />
        </td>
      </tr>
      {asking && (
        <tr className="subrow">
          <td colSpan={6}>
            <form
              className="confirmbar"
              onSubmit={(e) => {
                e.preventDefault();
                void accept.run();
              }}
            >
              <span>Accept this difference for promotion. It lapses if either side changes.</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note: why it is fine" aria-label="Acceptance note" maxLength={300} autoFocus />
              <button type="submit" className="btn sm pri" disabled={accept.busy || !note.trim()}>
                {accept.busy ? 'Accepting…' : 'Accept'}
              </button>
              <button type="button" className="btn sm" disabled={accept.busy} onClick={() => setAsking(false)}>
                Back
              </button>
              <InlineError error={accept.error} />
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

function DiffReport({ load, mode }: { load: ReturnType<typeof useDiff>; mode: CutoverState['mode'] }) {
  const [filter, setFilter] = useState<'open' | 'accepted' | 'all'>('open');
  const [limit, setLimit] = useState(200);
  const d = load.data;
  const items = d ? d.items.filter((i) => (filter === 'all' ? true : filter === 'open' ? !i.accepted : !!i.accepted)) : [];
  return (
    <Card
      title={<><Icon name="split" />Difference report · shadow vs live</>}
      right={
        <>
          {d && <span className="note">Generated {fmtLocal(d.generatedAt)}</span>}
          <RefreshBtn onClick={load.reload} busy={load.loading} />
        </>
      }
    >
      {load.error && !d ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not build the difference report" />
      ) : !d ? (
        <Loading label="Comparing shadow and live…" />
      ) : (
        <>
          <div className="erp-kv">
            <span><span className="lbl">Live</span> <b>{num(d.counts.live)}</b></span>
            <span><span className="lbl">Shadow</span> <b>{num(d.counts.shadow)}</b></span>
            <span><span className="lbl">Same</span> <b>{num(d.counts.same)}</b></span>
            <span><span className="lbl">Different</span> <b>{num(d.counts.different)}</b></span>
            <span><span className="lbl">Only in live</span> <b>{num(d.counts.onlyInLive)}</b></span>
            <span><span className="lbl">Only in shadow</span> <b>{num(d.counts.onlyInShadow)}</b></span>
            <span><span className="lbl">Accepted</span> <b>{num(d.counts.accepted)}</b></span>
            <span><span className="lbl">Open</span> <b className={d.counts.open ? 'warn-t' : 'ok-t'}>{num(d.counts.open)}</b></span>
          </div>
          {d.ignoredFields.length > 0 && (
            <div className="note">
              Not compared: <span className="mono">{d.ignoredFields.join(', ')}</span> (each system's own save time and ERP links of import-only records).
            </div>
          )}
          {mode === 'off' && d.counts.shadow === 0 ? (
            <Empty>No shadow copy yet. Start shadow sync to fill it.</Empty>
          ) : (
            <>
              <Seg
                label="Show differences"
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'open', label: `Open ${num(d.counts.open)}` },
                  { value: 'accepted', label: `Accepted ${num(d.counts.accepted)}` },
                  { value: 'all', label: 'All' },
                ]}
              />
              {items.length === 0 ? (
                <Empty>{filter === 'open' ? 'No open differences.' : 'Nothing to show.'}</Empty>
              ) : (
                <div className="tw">
                  <table className="erp-t">
                    <thead>
                      <tr>
                        <th scope="col">Kind</th>
                        <th scope="col">Entity</th>
                        <th scope="col">Site</th>
                        <th scope="col">Fields (live → shadow)</th>
                        <th scope="col">Accepted</th>
                        <th scope="col">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.slice(0, limit).map((it) => (
                        <DiffRow key={`${it.entityType}:${it.entityId}`} it={it} onDone={load.reload} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {items.length > limit && (
                <div className="tfoot">
                  <span className="note">
                    Showing {num(limit)} of {num(items.length)}
                  </span>
                  <span className="sp" />
                  <button type="button" className="btn sm" onClick={() => setLimit((n) => n + 500)}>
                    Show more
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}

/* ---------- history ---------- */

function summaryText(s: CutoverState['history'][number]['summary']): string {
  if (!s) return '';
  return Object.entries(s)
    .filter(([k, v]) => k !== 'holdIds' && v !== null && v !== undefined)
    .map(([k, v]) => `${human(k.replace(/([A-Z])/g, '_$1').toLowerCase()).toLowerCase()} ${typeof v === 'string' && /^\d{4}-\d\d-\d\dT/.test(v) ? fmtLocal(v) : String(v)}`)
    .join(' · ');
}

function History({ c }: { c: CutoverState }) {
  return (
    <Card title={<><Icon name="log" />Cutover history</>}>
      {c.history.length === 0 ? (
        <Empty>No cutover actions yet.</Empty>
      ) : (
        <div>
          {c.history.map((h) => (
            <div key={h.id} className="trow">
              <span className="mono dim nowrap">{fmtLocal(h.at)}</span>
              <span className="strong">{human(h.action)}</span>
              <span className="note ell-n">{summaryText(h.summary)}</span>
              <span className="sp" />
              <span className="mono dim small">{h.by ?? 'system'}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ---------- tab ---------- */

const useDiff = () => useLoad((signal) => api.cutoverDiff(signal), []);

export function CutoverTab() {
  const state = useLoad((signal) => api.cutover(signal), []);
  const status = useLoad((signal) => api.erpStatus(signal), []);
  const diff = useDiff();
  const reloadAll = () => {
    state.reload();
    status.reload();
    diff.reload();
  };
  const c = state.data;
  // While the shadow copy fills (and the ERP team fixes data), keep the steps and the report current.
  usePoll(() => {
    if (c?.mode === 'shadow') reloadAll();
  }, 5000);
  return (
    <div className="page">
      <ErpHeader
        tab="cutover"
        sub="Shadow sync, difference report, one-step promotion, and a 30-day rollback. Monitoring keeps running on the imported data until the ERP data is proven to match."
        actions={<RefreshBtn onClick={reloadAll} busy={state.loading} />}
      />
      {state.error && !c ? (
        <ErrorBox error={state.error} onRetry={state.reload} title="Could not load the cutover state" />
      ) : !c ? (
        <Loading label="Loading the cutover state…" />
      ) : (
        <>
          <ModeStrip feed={c} rollbackAvailable={c.rollbackAvailable} />
          {status.error ? <div className="note warn-t">Status unavailable: {errorText(status.error)}</div> : null}
          <Stepper c={c} diff={diff.data} status={status.data} onDone={reloadAll} />
          {c.mode !== 'live' && <DiffReport load={diff} mode={c.mode} />}
          <History c={c} />
        </>
      )}
    </div>
  );
}
